import Form, {
  ButtonItem,
  ButtonOptions,
  EmailRule,
  Item,
  RequiredRule,
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import notify from "../../lib/notify";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth";
import { Enable2FA } from "../enable2fa/Enable2FA";
import { LockOutDisableLoginForm } from "../lockout-disable-login-form/LockoutDisableLoginForm";
import { TwoFaLoginForm } from "../twofa-login-form/TwoFaLoginForm";
import "./LoginForm.scss";

const companies = [
  { id: 1, name: "Invenio" },
  { id: 2, name: "Invenio QD" },
  { id: 3, name: "Quality & Human Services" },
  { id: 4, name: "IPSUPPORT" },
];

type Mode = "credentials" | "2fa" | "2fa-enable" | "lockout";

type StoredCredentials = {
  email: string;
  password: string;
  companyId: number;
  rememberMe: boolean;
};

export const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const { t } = useTranslation();

  const formData = useRef({
    email: "",
    password: "",
    companyId: null as number | null,
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("credentials");
  const [stored, setStored] = useState<StoredCredentials | null>(null);
  const autoSignInTriggered = useRef(false);

  const handleSignInResult = useCallback(
    (result: any, creds: StoredCredentials) => {
      if (result.requiresTwoFactorAuthentication) {
        setStored(creds);
        setMode("2fa");
      } else if (result.enable2Fa) {
        setStored(creds);
        setMode("2fa-enable");
      } else if (result.isLockedOut) {
        setStored(creds);
        setMode("lockout");
      } else if (result.confirmationRequired) {
        navigate("/first-login", {
          state: {
            email: creds.email,
            password: creds.password,
            companyId: creds.companyId,
          },
        });
      } else if (result.firstChangePassword) {
        navigate("/first-change-password", {
          state: {
            email: creds.email,
            password: creds.password,
            companyId: creds.companyId,
            rememberMe: creds.rememberMe,
          },
        });
      } else if (result.peselLoginBlock) {
        notify(t("accountBlocked"), "error", 5000);
      } else {
        navigate("/");
      }
    },
    [navigate, t],
  );

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const { email, password, companyId, rememberMe } = formData.current;

      if (!companyId || companyId === 0) {
        notify(t("selectCompanyRequired"), "warning");
        return;
      }

      setLoading(true);

      try {
        const result = await signIn(email, password, companyId, rememberMe);

        if (result.isOk) {
          handleSignInResult(result, {
            email,
            password,
            companyId,
            rememberMe,
          });
        } else {
          notify(result.message || t("common.unknownErrorOccurred"), "error", 5000);
        }
      } catch (error) {
        notify(t("common.unknownErrorOccurred"), "error", 5000);
      } finally {
        setLoading(false);
      }
    },
    [signIn, t, handleSignInResult],
  );

  useEffect(() => {
    const state = location.state as
      | {
          autoSignIn?: boolean;
          email?: string;
          password?: string;
          companyId?: number;
          rememberMe?: boolean;
        }
      | null;

    if (
      !state?.autoSignIn ||
      autoSignInTriggered.current ||
      !state.email ||
      !state.password ||
      !state.companyId
    ) {
      return;
    }

    autoSignInTriggered.current = true;
    const creds: StoredCredentials = {
      email: state.email,
      password: state.password,
      companyId: state.companyId,
      rememberMe: state.rememberMe ?? false,
    };

    (async () => {
      setLoading(true);
      try {
        const result = await signIn(
          creds.email,
          creds.password,
          creds.companyId,
          creds.rememberMe,
        );
        if (result.isOk) {
          handleSignInResult(result, creds);
        } else {
          notify(result.message || t("common.unknownErrorOccurred"), "error", 5000);
        }
      } finally {
        setLoading(false);
        navigate(location.pathname, { replace: true });
      }
    })();
  }, [location, signIn, handleSignInResult, navigate, t]);

  const handle2FAEnabled = useCallback(async () => {
    if (!stored) {
      setMode("credentials");
      return;
    }
    setLoading(true);
    try {
      const result = await signIn(
        stored.email,
        stored.password,
        stored.companyId,
        stored.rememberMe,
      );
      if (result.isOk) {
        handleSignInResult(result, stored);
      } else {
        notify(result.message || t("common.unknownErrorOccurred"), "error", 5000);
        setMode("credentials");
      }
    } finally {
      setLoading(false);
    }
  }, [signIn, stored, handleSignInResult, t]);

  const backToCredentials = useCallback(() => {
    setStored(null);
    setMode("credentials");
  }, []);

  if (mode === "2fa") {
    return (
      <TwoFaLoginForm
        recoveryLink="/recovery-code-login"
        rememberMe={stored?.rememberMe ?? false}
      />
    );
  }

  if (mode === "2fa-enable" && stored) {
    return (
      <Enable2FA
        email={stored.email}
        companyId={stored.companyId}
        onEnabled={handle2FAEnabled}
        onBack={backToCredentials}
      />
    );
  }

  if (mode === "lockout" && stored) {
    return (
      <LockOutDisableLoginForm
        email={stored.email}
        companyId={stored.companyId}
        rememberMe={stored.rememberMe}
        onTwoFactorRequired={(rememberMe) => {
          setStored({ ...stored, rememberMe });
          setMode("2fa");
        }}
      />
    );
  }

  return (
    <form className="login-form" onSubmit={onSubmit}>
      <Form
        formData={formData.current}
        disabled={loading}
        showColonAfterLabel
        showRequiredMark
      >
        <Item
          dataField="email"
          editorType="dxTextBox"
          editorOptions={{
            stylingMode: "filled",
            placeholder: t("emailAddress"),
            mode: "email",
          }}
          label={{
            visible: false,
          }}
        >
          <RequiredRule />
          <EmailRule />
        </Item>
        <Item
          dataField="password"
          editorType="dxTextBox"
          editorOptions={{
            stylingMode: "filled",
            placeholder: t("password"),
            mode: "password",
          }}
          label={{
            visible: false,
          }}
        >
          <RequiredRule />
        </Item>
        <Item
          dataField="companyId"
          editorType="dxSelectBox"
          editorOptions={{
            items: companies,
            displayExpr: "name",
            valueExpr: "id",
            placeholder: t("company"),
            stylingMode: "filled",
          }}
          label={{
            visible: false,
          }}
        >
          <RequiredRule />
        </Item>
        <Item
          dataField="rememberMe"
          editorType="dxCheckBox"
          editorOptions={{
            text: t("rememberMe"),
          }}
          label={{
            visible: false,
          }}
        />
        <ButtonItem>
          <ButtonOptions width="100%" type="default" useSubmitBehavior>
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width="24px" height="24px" visible />
              ) : (
                t("signIn")
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
};
